/** PROTOTYPE variant A — inline Update / Keep HRP / Ignore actions (evolution of current table). */

import {
    ACTION_LABEL,
    AmountCell,
    MatchBadge,
    getExtractedLabel,
    getNewAmount,
    getRowStatus,
    showCreateButton,
    showIgnoreButton,
    showKeepButton,
    showUpdateButton,
} from "./hrpReconciliationPrototypeShared";

const RowActions = ({ row, onUpdate, onKeep, onIgnore, onCreate }) => {
    if (row.confirmed || row.skipped) return null;
    return (
        <div className="billing-rate-builder__hrp-reconciliation-actions">
            {showUpdateButton(row) && (
                <button type="button" className="btn edit-btn" onClick={() => onUpdate(row.rowId)}>
                    Update
                </button>
            )}
            {showKeepButton(row) && (
                <button type="button" className="btn billing-rate-builder__prototype-btn-keep" onClick={() => onKeep(row.rowId)}>
                    Keep HRP
                </button>
            )}
            {showCreateButton(row) && (
                <button type="button" className="btn edit-btn" onClick={() => onCreate(row.rowId)}>
                    Create
                </button>
            )}
            {showIgnoreButton(row) && (
                <button type="button" className="btn" onClick={() => onIgnore(row.rowId)}>
                    Ignore
                </button>
            )}
        </div>
    );
};

const HrpReconciliationVariantA = ({ rows, hrpSnapshot, onUpdate, onKeep, onIgnore, onCreate }) => {
    const pending = rows.filter((r) => !r.confirmed && !r.skipped);
    const asOfLabel = hrpSnapshot?.asOfDate ? ` as of ${hrpSnapshot.asOfDate}` : "";

    return (
        <div className="billing-rate-builder__alert billing-rate-builder__hrp-reconciliation">
            <div className="billing-rate-builder__page-header">
                <div>
                    <p className="title billing-rate-builder__alert-title">HRP reconciliation{asOfLabel}</p>
                    <p className="billing-rate-builder__alert-body-plain">
                        Compare HRP with extracted fees. For mismatches, choose <strong>Update</strong> (apply document
                        values) or <strong>Keep HRP</strong> (preserve existing HRP component).
                        {pending.length > 0 ? ` ${pending.length} pending.` : " All rows resolved."}
                    </p>
                </div>
            </div>

            <div className="billing-rate-builder__hrp-reconciliation-table-wrap">
                <table className="billing-rate-builder__hrp-reconciliation-table">
                    <thead>
                        <tr>
                            <th>Link type</th>
                            <th>In HRP</th>
                            <th>Extracted</th>
                            <th>New</th>
                            <th>Action</th>
                            <th>Status</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const action = row.userAction ?? row.suggestedAction;
                            const extractedLabel = getExtractedLabel(row);
                            const isIgnored = row.skipped || row.userAction === "skip";

                            return (
                                <tr
                                    key={row.rowId}
                                    className={[
                                        row.nameMismatch && !row.confirmed ? "billing-rate-builder__prototype-row--mismatch" : "",
                                        isIgnored ? "billing-rate-builder__prototype-row--ignored" : "",
                                        row.hrpKeepApplied ? "billing-rate-builder__prototype-row--kept" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    <td>
                                        <MatchBadge matchType={row.matchType} />
                                        {row.matchScore != null && row.matchType?.includes("fuzzy") && (
                                            <div className="billing-rate-builder__muted">
                                                {Math.round(row.matchScore * 100)}% match
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {row.hrpComponentName ? (
                                            <>
                                                <strong>{row.hrpComponentName}</strong>
                                                {row.hrpBillingMethod && (
                                                    <div className="billing-rate-builder__muted">{row.hrpBillingMethod}</div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="billing-rate-builder__muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {extractedLabel ? (
                                            <>
                                                <strong>{extractedLabel}</strong>
                                                {row.nameMismatch && !row.hrpKeepApplied && (
                                                    <div className="billing-rate-builder__prototype-warning">
                                                        Name differs from HRP
                                                    </div>
                                                )}
                                                {row.hrpKeepApplied && (
                                                    <div className="billing-rate-builder__prototype-kept-badge">Kept from HRP</div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="billing-rate-builder__muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        <AmountCell amount={getNewAmount(row)} feeType={row.extractedFeeType} />
                                    </td>
                                    <td>{ACTION_LABEL[action] ?? action}</td>
                                    <td>{getRowStatus(row)}</td>
                                    <td>
                                        <RowActions
                                            row={row}
                                            onUpdate={onUpdate}
                                            onKeep={onKeep}
                                            onIgnore={onIgnore}
                                            onCreate={onCreate}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default HrpReconciliationVariantA;
